 import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { getCompanyName } from '../../lib/tenant';
import { useAuth } from '../../components/AuthContext';
import { canSeeInventoryCost, canSeeLedgerAllBranches, canSeeBranchComparison as canSeeBranchComparisonPermission, isAdminManagerOrExecutive } from '../../lib/permissions';
import OnboardingCallout from '../../components/OnboardingCallout';
import {
  IconAdjustmentsHorizontal,
  IconChevronDown,
  IconChevronUp,
  IconFileSpreadsheet,
  IconFileText,
  IconX,
} from '@tabler/icons-react';
import TransactionDetailPanel from './TransactionDetailPanel';
import { CORRECTED_LABEL, isCorrectionTakeBack } from '../../lib/corrections';
import LedgerExportSheet from '../../components/inventory/LedgerExportSheet';
import { categoryLabelText } from '../../lib/categoryLabel';
import { ledgerCostState, ledgerReference, applySearch } from '../../lib/ledgerExport';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];
const branchNameOf = id => BRANCHES.find(b => b.id === Number(id))?.name || '';

const TX_TYPES = [
  { value: '',         label: 'All types' },
  { value: 'receive',  label: 'Receive' },
  { value: 'consume',  label: 'Consume' },
  { value: 'adjust',   label: 'Adjust' },
  { value: 'transfer_in',  label: 'Transfer in' },
  { value: 'transfer_out', label: 'Transfer out' },
  // #371, Session 79.6, gate D23. The gate is explicit that a new movement
  // type needs a badge, a label, a filter option AND a signed display in the
  // same change, because "a type the Ledger cannot render is a movement
  // nobody can find". Called 'Made' rather than 'Production in' -- the
  // people reading this are on a shop floor.
  { value: 'production_in', label: 'Made' },
  // #694, Session 79.22b.4.6. An Undo is its own row that cancels an earlier
  // one - SAP's reversal document - so it needs the same four things D23 asked
  // for: a filter, a badge, a label and a signed display.
  { value: 'reversal', label: 'Undone' },
];

const DATE_RANGES = [
  { value: 'this_month', label: 'This month' },
  { value: 'today',      label: 'Today' },
  { value: 'this_week',  label: 'This week' },
  { value: 'last_month', label: 'Last month' },
  { value: 'all_time',   label: 'All time' },
];

const TX_TYPE_STYLES = {
  receive:      { bg: '#E1F5EE', color: '#085041', label: 'Receive' },
  consume:      { bg: '#FAECE7', color: '#712B13', label: 'Consume' },
  adjust:       { bg: '#EEEDFE', color: '#3C3489', label: 'Adjust' },
  transfer_in:  { bg: '#E6F1FB', color: '#0C447C', label: 'Transfer in' },
  transfer_out: { bg: '#FAEEDA', color: '#633806', label: 'Transfer out' },
  // Green like receive, because it IS an inflow -- stock that did not exist
  // an hour ago now does. #371 D23.
  production_in: { bg: '#E1F5EE', color: '#085041', label: 'Made' },
  // Grey, because an Undo is neither an inflow nor an outflow of its own: it
  // takes back the row it names. 5.6 against its tint (#694).
  reversal:     { bg: '#F1EFE8', color: '#5F5E5A', label: 'Undone' },
};

// #761 / gate B6, Session 79.22b.6c.3 -- a take-back a correction wrote reads
// Corrected, not Undone, in the list, the export and the detail panel.
function txStyleFor(tx) {
  const ts = TX_TYPE_STYLES[tx.tx_type] || { bg: '#F0F0F0', color: '#5C5C5C', label: tx.tx_type };
  return isCorrectionTakeBack(tx) ? { ...ts, label: CORRECTED_LABEL } : ts;
}

const ROWS_OPTIONS = [25, 50, 100];

function getDateRange(value) {
  const now = new Date();
  const start = new Date();
  if (value === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (value === 'this_week') {
    const day = now.getDay();
    start.setDate(now.getDate() - day);
    start.setHours(0, 0, 0, 0);
  } else if (value === 'this_month') {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
  } else if (value === 'last_month') {
    start.setMonth(now.getMonth() - 1);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: start.toISOString(), end: end.toISOString() };
  } else {
    return { start: null, end: null };
  }
  return { start: start.toISOString(), end: null };
}

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// #343 D9's cost rule and its money formatter now live in src/lib/ledgerExport.js
// (#624, Session 79.22b): the detail panel needs them, and the panel is imported
// by this page, so it could not import them from here without a loop.

function fmtQty(qty, txType) {
  if (qty === null || qty === undefined) return '—';
  const n = Number(qty);
  if (txType === 'receive' || txType === 'transfer_in' || txType === 'production_in') return `+${Math.abs(n)}`;
  if (txType === 'consume' || txType === 'transfer_out') return `−${Math.abs(n)}`;
  // An Undo carries its own sign, like adjust: minus when it takes back
  // something made, plus when it puts back something sent (#694).
  if (txType === 'adjust' || txType === 'reversal') return n >= 0 ? `+${n}` : `−${Math.abs(n)}`;
  return String(n);
}


function getActiveFilterCount(filters) {
  let count = 0;
  if (filters.branchId !== null) count++;
  if (filters.dateRange !== 'this_month') count++;
  if (filters.txType !== '') count++;
  if (filters.userId !== '') count++;
  if (filters.search !== '') count++;
  return count;
}

export default function InventoryLedgerPage({ setActive }) {
  // #488, Session 78w — isPurchasing added. canSeeInventoryCost now reaches
  // accounts payable through canReconcileInvoice, which reads it.
  const { profile, isAdmin, isManager, isExecutive, isTeamMember, isPurchasing } = useAuth();
  const isMobile = window.innerWidth <= 768;
  // Callouts gate Wave 3 — inv_ledger_export_first. Manager and above per the
  // gate's role column. Deliberately its own name rather than reusing
  // canSeeInventoryCost, which is the same predicate answering a different
  // question; two things being true for the same roles today is not a reason to
  // conflate them.
  const canSeeExportCallout = isAdminManagerOrExecutive({ isAdmin, isManager, isExecutive });
  const exportBtnRef = useRef(null);
  // #488 — whole auth object, not the manager triple. Passing only the triple
  // hands the predicate an undefined profile and AP silently gets nothing.
  const canSeeCost = canSeeInventoryCost({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  // #370, Session 78j. This used to call canSeeAllBranches, which widened that
  // session from isAdminOrExecutive to isStaffWithPurchasing for the Stock Home
  // workbench. The Ledger's answer to "may this person see every branch" is not
  // the same question as Stock Home's, and it governs the export path as well as
  // the view -- fetchAllForExport pulls every row the flag allows.
  // #153, Session 297: Colby ruled Purchasing in (R1); branch managers stay out.
  const canSeeAllBranches = canSeeLedgerAllBranches({ isAdmin, isExecutive, isPurchasing });
  const canSeeBranchComparison = canSeeBranchComparisonPermission({ isAdmin, isExecutive });

  const userBranchId = profile?.branch_id;

  const [filters, setFilters] = useState({
    branchId: userBranchId || null,
    dateRange: 'this_month',
    txType: '',
    userId: '',
    search: '',
  });

  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  // #394, Session 78n7. One export control replaces two silent buttons. The
  // old pair each re-ran the query with NO row limit at all, so PostgREST's own
  // ceiling applied and produced a well-formed, professional, incomplete file
  // whose printed row count agreed with the truncated set.
  const [showExport, setShowExport] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ received: 0, consumed: 0, net: 0, costUsed: 0 });
  const [branchStats, setBranchStats] = useState([]);
  const [selectedTx, setSelectedTx] = useState(null);

  function setFilter(key, val) {
    setFilters(prev => ({ ...prev, [key]: val }));
    setPage(1);
  }

  function removeFilter(key) {
    const defaults = { branchId: userBranchId, dateRange: 'this_month', txType: '', userId: '', search: '' };
    setFilters(prev => ({ ...prev, [key]: defaults[key] }));
    setPage(1);
  }

  useEffect(() => {
    if (profile?.branch_id && filters.branchId === null) {
      setFilters(prev => ({ ...prev, branchId: profile.branch_id }));
    }
  }, [profile]);

  const loadUsers = useCallback(async () => {
    const { data } = await supabase
      .from('users')
      .select('id, full_name')
      .eq('is_active', true)
      .order('full_name');
    setUsers(data || []);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // 74s. The flat category list, so a transaction's category can be resolved to its
  // parent without the self-referencing embed that never worked.
  const loadCategories = useCallback(async () => {
    const { data } = await supabase
      .from('inventory_categories')
      .select('id, name, parent_id')
      .order('name');
    setCategories(data || []);
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const loadTransactions = useCallback(async () => {
    if (!profile) return;
    setLoading(true);
    setError('');
    try {
      const { start, end } = getDateRange(filters.dateRange);

      let query = supabase
        .from('inventory_transactions')
        .select(`
          id, tx_type, qty, qty_after, work_order_ref, work_order_ref_note, notes, created_at, branch_id, packing_slip_url, correction_id,
          transfer_number, counterparty_branch_id, request_number,
          unit_cost_at_transaction, unit_cost_settled,
          inventory_items ( id, name, sku, category_id,
            inventory_categories ( id, name, parent_id )
          ),
          users ( id, full_name )
        `, { count: 'exact' })
        .order('created_at', { ascending: false });

      if (!canSeeAllBranches) {
        query = query.eq('branch_id', userBranchId);
      } else if (filters.branchId) {
        query = query.eq('branch_id', filters.branchId);
      }

      if (filters.txType) query = query.eq('tx_type', filters.txType);
      if (filters.userId) query = query.eq('user_id', filters.userId);
      if (start) query = query.gte('created_at', start);
      if (end) query = query.lt('created_at', end);

      const from = (page - 1) * rowsPerPage;
      const to = from + rowsPerPage - 1;
      query = query.range(from, to);

      const { data, error: txErr, count } = await query;
      if (txErr) throw txErr;

      // #633 -- ONE search rule with the export, which also matches the
      // transfer and request numbers the rows now name.
      const filtered = applySearch(data || [], filters.search);

      setTransactions(filtered);
      setTotalCount(count || 0);

      await loadStats(start, end);
      if (canSeeBranchComparison && !filters.branchId) {
        await loadBranchStats(start, end);
      } else {
        setBranchStats([]);
      }
    } catch (e) {
      setError('Failed to load transactions: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage, profile, canSeeAllBranches, canSeeBranchComparison]);

  async function loadStats(start, end) {
    let query = supabase
      .from('inventory_transactions')
      .select('tx_type, qty');

    if (!canSeeAllBranches) {
      query = query.eq('branch_id', userBranchId);
    } else if (filters.branchId) {
      query = query.eq('branch_id', filters.branchId);
    }
    if (filters.txType) query = query.eq('tx_type', filters.txType);
    if (filters.userId) query = query.eq('user_id', filters.userId);
    if (start) query = query.gte('created_at', start);
    if (end) query = query.lt('created_at', end);

    const { data } = await query;
    if (!data) return;

    let received = 0, consumed = 0;
    data.forEach(tx => {
      const q = Math.abs(Number(tx.qty));
      // production_in counts as an inflow and its paired transfer_out as an
      // outflow, so a made-and-shipped run nets to zero ON HAND while BOTH
      // flows stay visible. That pair is what gives a factory branch a
      // production history it has never had. #371 D23.
      if (tx.tx_type === 'receive' || tx.tx_type === 'transfer_in' || tx.tx_type === 'production_in') received += q;
      if (tx.tx_type === 'consume' || tx.tx_type === 'transfer_out') consumed += q;
      // #694 -- AN UNDO NETS AGAINST WHAT IT CANCELS rather than counting as a
      // movement of its own: an undone sign-off comes off "received" and an
      // undone send off "consumed", so a mistaken press and its Undo come to
      // nothing instead of reading as something made AND something used.
      if (tx.tx_type === 'reversal') {
        if (Number(tx.qty) < 0) received -= q; else consumed -= q;
      }
    });
    setStats({ received, consumed, net: received - consumed, costUsed: 0 });
  }

  async function loadBranchStats(start, end) {
    let query = supabase
      .from('inventory_transactions')
      .select('tx_type, qty, branch_id');
    if (start) query = query.gte('created_at', start);
    if (end) query = query.lt('created_at', end);
    const { data } = await query;
    if (!data) return;

    const map = {};
    BRANCHES.forEach(b => { map[b.id] = { received: 0, consumed: 0 }; });
    data.forEach(tx => {
      if (!map[tx.branch_id]) return;
      const q = Math.abs(Number(tx.qty));
      if (tx.tx_type === 'receive' || tx.tx_type === 'transfer_in' || tx.tx_type === 'production_in') map[tx.branch_id].received += q;
      if (tx.tx_type === 'consume' || tx.tx_type === 'transfer_out') map[tx.branch_id].consumed += q;
      // #694 -- as in loadStats: an Undo nets against what it cancels.
      if (tx.tx_type === 'reversal') {
        if (Number(tx.qty) < 0) map[tx.branch_id].received -= q; else map[tx.branch_id].consumed -= q;
      }
    });
    setBranchStats(BRANCHES.map(b => ({ ...b, ...map[b.id], net: map[b.id].received - map[b.id].consumed })));
  }

  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const branchName = filters.branchId
    ? BRANCHES.find(b => b.id === filters.branchId)?.name || 'All branches'
    : 'All branches';

  const dateLabel = DATE_RANGES.find(d => d.value === filters.dateRange)?.label || 'This month';
  // #394 -- the export needs the same window the list uses, derived once here
  // rather than recomputed inside the sheet, so the file cannot describe a
  // different range from the screen that produced it.
  const { start: exportStart, end: exportEnd } = getDateRange(filters.dateRange);
  const txTypeLabel = filters.txType
    ? (TX_TYPE_STYLES[filters.txType]?.label || filters.txType)
    : 'All types';
  const userLabel = filters.userId
    ? (users.find(u => String(u.id) === String(filters.userId))?.full_name || 'Selected user')
    : 'All users';

  // #394. Built once and rendered in both the mobile and desktop trees below,
  // so the two surfaces cannot drift into offering different exports. This is
  // an element, not a component defined in a parent body -- locked rule 2 is
  // about the latter, which would remount on every render.
  const exportSheet = showExport ? (
    <LedgerExportSheet
      isMobile={isMobile}
      onClose={() => setShowExport(false)}
      filters={filters}
      start={exportStart}
      end={exportEnd}
      canSeeAllBranches={canSeeAllBranches}
      userBranchId={userBranchId}
      canSeeCost={canSeeCost}
      branchLabel={branchName}
      dateLabel={dateLabel}
      txTypeLabel={txTypeLabel}
      userLabel={userLabel}
      branchName={id => BRANCHES.find(b => b.id === id)?.name || ''}
      txLabel={(t, tx) => (tx && isCorrectionTakeBack(tx) ? CORRECTED_LABEL : (TX_TYPE_STYLES[t]?.label || t))}
      fmtDate={fmtDate}
      stats={stats}
      profile={profile}
    />
  ) : null;
  const activeFilterCount = getActiveFilterCount(filters);
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  function getQtyColor(qty, txType) {
    if (txType === 'receive' || txType === 'transfer_in' || txType === 'production_in') return '#0F6E56';
    if (txType === 'consume' || txType === 'transfer_out') return '#A32D2D';
    if (txType === 'adjust' || txType === 'reversal') return Number(qty) >= 0 ? '#0F6E56' : '#A32D2D';
    return 'var(--text-primary)';
  }

  // 74s. Was reading the nested self-embed, which returns children rather than the
  // parent, so this printed the subcategory alone on every ledger row. See
  // src/lib/categoryLabel.js.
  function getCategoryLabel(cat) {
    return categoryLabelText(cat, categories);
  }

  const activeChips = [];
  if (filters.branchId && filters.branchId !== userBranchId) {
    activeChips.push({ key: 'branchId', label: branchName });
  } else if (filters.branchId === userBranchId && canSeeAllBranches) {
    activeChips.push({ key: 'branchId', label: branchName });
  }
  if (filters.dateRange !== 'this_month') {
    activeChips.push({ key: 'dateRange', label: dateLabel });
  }
  if (filters.txType) {
    activeChips.push({ key: 'txType', label: TX_TYPE_STYLES[filters.txType]?.label || filters.txType });
  }

  // ── MOBILE ────────────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 80 }}>
        {exportSheet}

        <OnboardingCallout
          calloutKey="inv_ledger_export_first"
          text="Export gives you exactly what the filters above show, not the whole ledger."
          anchorRef={null}
          isMobile={true}
          show={canSeeExportCallout}
        />

        <div style={{ background: 'white', padding: '10px 14px', borderBottom: '1px solid #E8E8E8', position: 'sticky', top: 'var(--topbar-height)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F' }}>Transaction ledger</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setShowExport(true)} style={{ height: 28, padding: '0 10px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 11, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#0F0F0F' }}>
                <IconFileSpreadsheet size={13} /> Export
              </button>
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#7A7A7A' }}>{branchName} · {dateLabel} · {totalCount} transactions</div>
        </div>

        {/* Session 79.14, Colby: "look at the cards. half the space is blank. all three
              could easily fit across one row below the page title bar". He is right, and
              the desktop branch of this same page has ALWAYS used repeat(3,1fr) - so the
              phone was the odd one out, wrapping the third card onto a row of its own and
              leaving half of it empty. Three columns, and the labels are shortened to fit:
              "Net change" would not sit on one line at a third of 375px. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, padding: '12px 12px 0' }}>
          {[
            { label: 'Received', val: `+${stats.received}`, color: '#0F6E56' },
            { label: 'Consumed', val: `−${stats.consumed}`, color: '#A32D2D' },
            { label: 'Net change', val: stats.net >= 0 ? `+${stats.net}` : `−${Math.abs(stats.net)}`, color: stats.net >= 0 ? '#0C447C' : '#A32D2D' },
            /* Issue #24: cost-of-goods not real yet — do not render until cost tracking ships */
          ].map((s, i) => (
            <div key={i} style={{ background: 'white', borderRadius: 8, padding: '10px 8px', border: '1px solid #E8E8E8' }}>
              <div style={{ fontSize: 10, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 2, whiteSpace: 'nowrap' }}>{s.label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color }}>{s.val}</div>
            </div>
          ))}
        </div>

        <div style={{ padding: '10px 12px 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setFiltersExpanded(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 5, height: 32, padding: '0 12px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 12, background: 'white', cursor: 'pointer', color: '#0F0F0F' }}
          >
            <IconAdjustmentsHorizontal size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: '#1D9E75', color: 'white', fontSize: 9, fontWeight: 700 }}>{activeFilterCount}</span>
            )}
            {filtersExpanded ? <IconChevronUp size={11} color="#7A7A7A" /> : <IconChevronDown size={11} color="#7A7A7A" />}
          </button>
          {!filtersExpanded && activeChips.map(chip => (
            <span key={chip.key} onClick={() => removeFilter(chip.key)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 26, padding: '0 9px', borderRadius: 20, background: '#E1F5EE', border: '0.5px solid #5DCAA5', fontSize: 11, color: '#085041', cursor: 'pointer' }}>
              {chip.label} <IconX size={11} color="#0F6E56" />
            </span>
          ))}
        </div>

        {filtersExpanded && (
          <div style={{ margin: '8px 12px 0', background: 'white', border: '1px solid #E8E8E8', borderRadius: 8, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { label: 'Branch', content: canSeeAllBranches ? (
                <select value={filters.branchId || ''} onChange={e => setFilter('branchId', e.target.value ? parseInt(e.target.value) : null)}
                  style={{ width: '100%', height: 32, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', background: 'white', color: '#0F0F0F' }}>
                  <option value="">All branches</option>
                  {BRANCHES.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              ) : (
                <div style={{ height: 32, display: 'flex', alignItems: 'center', fontSize: 12, color: '#5C5C5C' }}>{branchName}</div>
              )},
              { label: 'Date range', content: (
                <select value={filters.dateRange} onChange={e => setFilter('dateRange', e.target.value)}
                  style={{ width: '100%', height: 32, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', background: 'white', color: '#0F0F0F' }}>
                  {DATE_RANGES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              )},
              { label: 'Type', content: (
                <select value={filters.txType} onChange={e => setFilter('txType', e.target.value)}
                  style={{ width: '100%', height: 32, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', background: 'white', color: '#0F0F0F' }}>
                  {TX_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              )},
              { label: 'User', content: (
                <select value={filters.userId} onChange={e => setFilter('userId', e.target.value)}
                  style={{ width: '100%', height: 32, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', background: 'white', color: '#0F0F0F' }}>
                  <option value="">All users</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              )},
              { label: 'Search', content: (
                <input type="text" value={filters.search} onChange={e => setFilter('search', e.target.value)} placeholder="Item name or job ref…"
                  style={{ width: '100%', height: 32, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', color: '#0F0F0F' }} />
              )},
            ].map(({ label, content }) => (
              <div key={label}>
                <div style={{ fontSize: 10, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>{label}</div>
                {content}
              </div>
            ))}
          </div>
        )}

        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.5px', padding: '2px 0' }}>
            Sorted by date · newest first
          </div>

          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
          ) : error ? (
            <div style={{ background: '#FFEBEE', color: '#A32D2D', borderRadius: 8, padding: '10px 12px', fontSize: 12 }}>{error}</div>
          ) : transactions.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 10, padding: '24px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#9A9A9A' }}>No transactions found</div>
              <div style={{ fontSize: 11, color: '#C0C0C0', marginTop: 4 }}>Try adjusting your filters</div>
            </div>
          ) : transactions.map(tx => {
            const ts = txStyleFor(tx);
            const qtyColor = getQtyColor(tx.qty, tx.tx_type);
            const branchLabel = BRANCHES.find(b => b.id === tx.branch_id)?.name || '—';
            return (
              <div key={tx.id} onClick={() => setSelectedTx(tx)} style={{ background: 'white', border: '1px solid #E8E8E8', borderRadius: 10, padding: '10px 12px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.inventory_items?.name || '—'}
                    </div>
                    <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 1 }}>
                      {getCategoryLabel(tx.inventory_items?.inventory_categories)}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: qtyColor }}>{fmtQty(tx.qty, tx.tx_type)}</div>
                    <div style={{ fontSize: 11, color: '#7A7A7A' }}>→ {tx.qty_after ?? '—'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 6, borderTop: '1px solid #F0F0F0' }}>
                  <span style={{ display: 'inline-block', padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: ts.bg, color: ts.color }}>{ts.label}</span>
                  <span style={{ fontSize: 11, color: '#7A7A7A' }}>{ledgerReference(tx, branchNameOf) || '—'} · {tx.users?.full_name?.split(' ').map((n,i) => i===0 ? n[0]+'.' : n).join(' ') || '—'}</span>
                  <span style={{ fontSize: 11, color: '#7A7A7A' }}>{fmtDate(tx.created_at).replace(', 2026','').replace(', 2025','')}</span>
                </div>
                {canSeeCost && (() => {
                  const c = ledgerCostState(tx);
                  // #624 -- 'none' returned null here, so the phone said nothing
                  // where the desk showed a dash. Now the card says so. On a
                  // PRODUCTION RUN it says the whole sentence, because a run with
                  // no cost means the item is unpriced - the Record production
                  // sheet warns about exactly this - while a receipt with no
                  // price is the ordinary gap #343 deliberately keeps quiet.
                  if (c.state === 'none') {
                    return (
                      <div style={{ marginTop: 6, fontSize: 11, color: '#5C5C5C' }}>
                        {tx.tx_type === 'production_in' ? c.title : `Cost ${c.text}`}
                      </div>
                    );
                  }
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700,
                        color: c.state === 'settled' ? '#0F0F0F' : '#8A6100',
                      }}>{c.text}</span>
                      <span style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: .3, padding: '1px 5px', borderRadius: 4,
                        background: c.state === 'settled' ? '#E1F5EE' : '#FFF4E0',
                        color: c.state === 'settled' ? '#085041' : '#9A5F00',
                      }}>{c.state === 'settled' ? 'SETTLED' : 'PROVISIONAL'}</span>
                    </div>
                  );
                })()}
              </div>
            );
          })}

          {totalCount > rowsPerPage && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 4 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                style={{ height: 32, padding: '0 14px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 12, background: 'white', color: '#0F0F0F', cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? .4 : 1 }}>
                ← Prev
              </button>
              <span style={{ fontSize: 12, color: '#7A7A7A', display: 'flex', alignItems: 'center' }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                style={{ height: 32, padding: '0 14px', border: '1px solid #E0E0E0', borderRadius: 8, fontSize: 12, background: 'white', color: '#0F0F0F', cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? .4 : 1 }}>
                Next →
              </button>
            </div>
          )}
        </div>
        {selectedTx && (
          <TransactionDetailPanel tx={selectedTx} onClose={() => setSelectedTx(null)} isMobile={isMobile} canSeeCost={canSeeCost} />
        )}
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Callouts gate Wave 3 — inv_ledger_export_first. fetchAllForExport()
          takes the live filters, so the export really is the filtered set; the
          research finding is that people export everything and re-filter in
          Excel because nothing on screen says so. */}
      <OnboardingCallout
        calloutKey="inv_ledger_export_first"
        text="Export gives you exactly what the filters above show, not the whole ledger."
        anchorRef={exportBtnRef}
        isMobile={false}
        show={canSeeExportCallout}
      />

      {exportSheet}

      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>
          <div className="page-title">Transaction ledger</div>
          <div className="page-sub">{branchName} · {dateLabel} · {totalCount} transactions</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button ref={exportBtnRef} className="btn btn-ghost btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 5 }} onClick={() => setShowExport(true)}>
            <IconFileSpreadsheet size={14} /> Export
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Received', val: `+${stats.received}`, color: '#0F6E56', sub: 'units this period' },
          { label: 'Consumed', val: `−${stats.consumed}`, color: '#A32D2D', sub: 'units this period' },
          { label: 'Net change', val: stats.net >= 0 ? `+${stats.net}` : `−${Math.abs(stats.net)}`, color: stats.net >= 0 ? '#0C447C' : '#A32D2D', sub: 'units this period' },
          /* Issue #24: cost-of-goods not real yet — do not render until cost tracking ships */
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--off-white)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 11, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-lt)', marginTop: 2 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: filtersExpanded ? 8 : 0 }}>
          <button
            onClick={() => setFiltersExpanded(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, background: 'var(--off-white)', cursor: 'pointer', color: 'var(--black)', flexShrink: 0 }}
          >
            <IconAdjustmentsHorizontal size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', background: '#1D9E75', color: 'white', fontSize: 9, fontWeight: 700 }}>{activeFilterCount}</span>
            )}
            {filtersExpanded ? <IconChevronUp size={11} color="var(--gray-mid)" /> : <IconChevronDown size={11} color="var(--gray-mid)" />}
          </button>

          {!filtersExpanded && activeChips.map(chip => (
            <span key={chip.key} onClick={() => removeFilter(chip.key)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 26, padding: '0 9px', borderRadius: 20, background: '#E1F5EE', border: '0.5px solid #5DCAA5', fontSize: 11, color: '#085041', cursor: 'pointer' }}>
              {chip.label} <IconX size={11} color="#0F6E56" />
            </span>
          ))}
        </div>

        {filtersExpanded && (
          <div style={{ background: 'var(--off-white)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Branch</div>
              {canSeeAllBranches ? (
                <select value={filters.branchId || ''} onChange={e => setFilter('branchId', e.target.value ? parseInt(e.target.value) : null)}
                  style={{ width: '100%', height: 30, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', background: 'white', color: 'var(--black)' }}>
                  <option value="">All branches</option>
                  {BRANCHES.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              ) : (
                <div style={{ height: 30, display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--gray-mid)' }}>{branchName}</div>
              )}
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Date range</div>
              <select value={filters.dateRange} onChange={e => setFilter('dateRange', e.target.value)}
                style={{ width: '100%', height: 30, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', background: 'white', color: 'var(--black)' }}>
                {DATE_RANGES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Type</div>
              <select value={filters.txType} onChange={e => setFilter('txType', e.target.value)}
                style={{ width: '100%', height: 30, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', background: 'white', color: 'var(--black)' }}>
                {TX_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>User</div>
              <select value={filters.userId} onChange={e => setFilter('userId', e.target.value)}
                style={{ width: '100%', height: 30, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', background: 'white', color: 'var(--black)' }}>
                <option value="">All users</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 10, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Search</div>
              <input type="text" value={filters.search} onChange={e => setFilter('search', e.target.value)} placeholder="Item name or job ref…"
                style={{ width: '100%', height: 30, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', color: 'var(--black)' }} />
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? (
            <div className="loading"><div className="spinner" />Loading...</div>
          ) : error ? (
            <div style={{ padding: '20px 16px', color: '#A32D2D', fontSize: 13 }}>{error}</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 100 }}>Type</th>
                  <th>Item</th>
                  <th style={{ width: 170 }}>Reference</th>
                  <th style={{ width: 110 }}>User</th>
                  <th style={{ width: 60, textAlign: 'right' }}>Qty</th>
                  <th style={{ width: 65, textAlign: 'right' }}>After</th>
                  {canSeeCost && <th style={{ width: 80, textAlign: 'right' }}>Cost</th>}
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan={canSeeCost ? 8 : 7}>
                    <div className="empty-state">
                      <div className="empty-state-icon">📋</div>
                      <div className="empty-state-title">No transactions found</div>
                      <div className="empty-state-sub">Try adjusting your filters.</div>
                    </div>
                  </td></tr>
                ) : transactions.map(tx => {
                  const ts = txStyleFor(tx);
                  const qtyColor = getQtyColor(tx.qty, tx.tx_type);
                  const branchLabel = BRANCHES.find(b => b.id === tx.branch_id)?.name || '—';
                  return (
                    <tr key={tx.id} onClick={() => setSelectedTx(tx)} style={{ cursor: 'pointer' }}>
                      <td style={{ color: 'var(--gray-mid)', fontSize: 12 }}>{fmtDate(tx.created_at)}</td>
                      <td><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: ts.bg, color: ts.color }}>{ts.label}</span></td>
                      <td>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{tx.inventory_items?.name || '—'}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-lt)' }}>
                          {tx.inventory_items?.sku || ''}{tx.inventory_items?.sku && ' · '}{getCategoryLabel(tx.inventory_items?.inventory_categories)}
                          {!filters.branchId && <span style={{ marginLeft: 4, color: 'var(--gray-lt)' }}>· {branchLabel}</span>}
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--gray-mid)' }}>{ledgerReference(tx, branchNameOf) || '—'}</td>
                      <td style={{ fontSize: 12, color: 'var(--gray-mid)' }}>{tx.users?.full_name || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: qtyColor }}>{fmtQty(tx.qty, tx.tx_type)}</td>
                      <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--gray-mid)' }}>{tx.qty_after ?? '—'}</td>
                      {canSeeCost && (() => {
                        const c = ledgerCostState(tx);
                        // #624 -- a production run with no cost SAYS so on the
                        // row; a tooltip needs a hover, which a finger never
                        // produces. Receipts keep the quiet dash (#343).
                        const runUnpriced = c.state === 'none' && tx.tx_type === 'production_in';
                        return (
                          <td title={c.title} style={{
                            textAlign: 'right', fontSize: 12, whiteSpace: runUnpriced ? 'normal' : 'nowrap',
                            color: c.state === 'settled' ? '#0F0F0F'
                                 : c.state === 'provisional' ? '#8A6100'
                                 : runUnpriced ? '#5C5C5C' : 'var(--gray-lt)',
                            fontWeight: c.state === 'settled' ? 700 : 400,
                          }}>
                            {runUnpriced ? 'No cost recorded' : c.text}
                            {c.state === 'provisional' && (
                              <span style={{ display: 'block', fontSize: 9, fontWeight: 700, letterSpacing: .3, color: '#9A5F00' }}>PROVISIONAL</span>
                            )}
                          </td>
                        );
                      })()}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--gray-mid)' }}>
            {totalCount === 0 ? '0 transactions' : `Showing ${(page - 1) * rowsPerPage + 1}–${Math.min(page * rowsPerPage, totalCount)} of ${totalCount}`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--gray-mid)' }}>Rows per page</span>
            <select value={rowsPerPage} onChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(1); }}
              style={{ height: 28, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', fontWeight: 600, color: 'var(--black)', background: 'white', cursor: 'pointer' }}>
              {ROWS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>

      {canSeeBranchComparison && !filters.branchId && branchStats.length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
            Branch comparison · {dateLabel}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, background: 'var(--border)' }}>
            {branchStats.map(b => (
              <div key={b.id} style={{ background: 'white', padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--black)', marginBottom: 6 }}>{b.name}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-mid)' }}>Received <span style={{ color: '#0F6E56', fontWeight: 700 }}>+{b.received}</span></div>
                <div style={{ fontSize: 11, color: 'var(--gray-mid)' }}>Consumed <span style={{ color: '#A32D2D', fontWeight: 700 }}>−{b.consumed}</span></div>
                <div style={{ fontSize: 11, color: 'var(--gray-mid)', marginTop: 4, paddingTop: 4, borderTop: '0.5px solid var(--border)' }}>
                  Net <span style={{ color: b.net >= 0 ? '#0C447C' : '#A32D2D', fontWeight: 700 }}>{b.net >= 0 ? '+' : '−'}{Math.abs(b.net)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {selectedTx && (
        <TransactionDetailPanel tx={selectedTx} onClose={() => setSelectedTx(null)} isMobile={isMobile} canSeeCost={canSeeCost} />
      )}
    </div>
  );
}